import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ApiService } from '@portal-core/auth/services/api.service';
import { ScriptService } from '@portal-core/general/services/script.service';
import { WINDOW } from '@portal-core/util/window.provider';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    gtag: Function;
    dataLayer: [];
  }
}

interface EventData {
  [key: string]: string | number | boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private routerEventsSubscription: Subscription;

  constructor(
    @Inject(WINDOW) private window: Window,
    private router: Router,
    private scriptService: ScriptService,
    private apiService: ApiService) { }

  init(trackingId: string) {
    if (!trackingId) {
      return;
    }

    // Load gtag.js asynchronously. Queued gtag() calls below land in
    // window.dataLayer and are processed once the script finishes loading.
    this.scriptService.inject(`https://www.googletagmanager.com/gtag/js?id=${trackingId}`, true);

    this.window.gtag = this.window.gtag || function () { (this.window.dataLayer = window.dataLayer || []).push(arguments); };
    this.window.gtag('js', new Date());
    // send_page_view: false — gtag('config') would otherwise auto-fire a
    // page_view that double-counts with the first NavigationEnd below. The
    // router subscription is the single source of truth for page views.
    this.window.gtag('config', trackingId, { send_page_view: false });

    // SPAs don't fire page reloads on route changes, so gtag.js's built-in
    // page tracking won't see navigations. Fire page_view manually on
    // every Angular NavigationEnd.
    this.routerEventsSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => {
      const url = (event as NavigationEnd).urlAfterRedirects;
      // page_location and page_title are GA4's standard page_view params.
      // page_path is a UA-era param that gtag.js still converts to dp= —
      // we pass it for an explicit path signal (more direct than letting
      // GA4 derive it from page_location). full_url is a custom param
      // preserved for legacy reports built against it.
      this.window.gtag('event', 'page_view', {
        page_location: this.window.location.href,
        page_title: this.window.document.title,
        page_path: url,
        full_url: `${this.window.location.host}${url}`,
        // instance_code is undefined on portal-login (no instance selected yet)
        // so only include it when available
        ...(this.apiService.centralInstanceInstanceCode && {
          instance_code: this.apiService.centralInstanceInstanceCode
        })
      });
    });
  }

  destroy() {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
      this.routerEventsSubscription = null;
    }
  }

  trackEvent(name: string, data: EventData) {
    this.window.gtag('event', name, data);
  }
}
